import {
  DataGridPremium,
  type DataGridPremiumProps,
  gridClasses,
  type GridValidRowModel,
} from '@mui/x-data-grid-premium'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import COLORS from 'lib/colors'
import GlobalStyles from '@mui/material/GlobalStyles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const DATA_GRID_SLOTS = {
  groupingCriteriaExpandIcon: ExpandMoreIcon,
  groupingCriteriaCollapseIcon: ExpandLessIcon,
  columnSortedAscendingIcon: ArrowDropUpIcon,
  columnSortedDescendingIcon: ArrowDropDownIcon,
}

const DATA_GRID_SLOT_PROPS = {
  filterPanel: {
    sx: { p: '12px' },
  },
  panel: {
    placement: 'bottom-start',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any,
}

const DATA_GRID_GROUPING_COL_DEF = {
  hideDescendantCount: true,
}

const DATA_GRID_BASE_SX = {
  border: 'none',
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: 'none',
  },
  '.MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
    pl: 4,
  },
  '.MuiDataGrid-cellEmpty': {
    padding: '0',
  },
  '.MuiDataGrid-columnHeaders': {
    fontSize: '12px',
    color: COLORS.slateGray,
  },
  '.MuiDataGrid-row:has(.MuiDataGrid-groupingCriteriaCell)': {
    fontWeight: 'bold',
    backgroundColor: COLORS.lightGray,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: COLORS.aliceBlue,
  },
  '.MuiDataGrid-aggregationColumnHeaderLabel': {
    fontSize: '10px',
    textTransform: 'lowercase',
  },
  '--DataGrid-cellOffsetMultiplier': 0,
} as const

type DataGridProps<R extends GridValidRowModel> = DataGridPremiumProps<R> & {
  error?: string | false | null
}

function ErrorOverlay({ error }: { error: string }) {
  return (
    <Stack direction='row' height='100%' alignItems='center' justifyContent='center'>
      <ErrorOutlineIcon sx={{ fontSize: 30, color: 'error.main', pl: 1, pr: .5 }}></ErrorOutlineIcon>
      <Typography color='error'>{error}</Typography>
    </Stack>
  )
}

export default function DataGrid<R extends GridValidRowModel>({
  columns,
  rows,
  rowCount,
  error = false,
  sx = {},
  ...props
}: DataGridProps<R>) {
  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiDataGrid-filterFormValueInput .MuiInputBase-root': {
            minHeight: '39px',
          },
        }}
      />

      <DataGridPremium
        hideFooterRowCount={!props.pagination}
        hideFooterSelectedRowCount={!props.pagination}
        columnHeaderHeight={props.columnHeaderHeight ?? 40}
        rowHeight={48}
        getAggregationPosition={(groupNode) => (groupNode == null ? null : 'inline')}
        groupingColDef={DATA_GRID_GROUPING_COL_DEF}
        disableRowSelectionOnClick
        // Things that are intended to be amended come after ...props
        {...props}
        // Shallow merging isn't great here, probably better to make this wrapper do less
        // when it comes to slots / slotProps and pass these in completely instead
        columns={columns}
        rows={error ? [] : rows}
        rowCount={error ? 0 : rowCount}
        slots={{
          ...DATA_GRID_SLOTS,
          ...props.slots,
          ...(error
            ? { noRowsOverlay: () => <ErrorOverlay error={error} /> }
            : {}),
        }}
        slotProps={{
          ...DATA_GRID_SLOT_PROPS,
          ...props.slotProps,
        }}
        sx={{
          ...DATA_GRID_BASE_SX,
          ...sx,
        }}
      />
    </>
  )
}
